import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  RefreshCw, Search, Plus, Pencil, Trash2, X, Eye, EyeOff, Save, Loader2, Copy,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, authHeader } from "@/context/AuthContext";
import AdminNav from "@/components/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = ["Non Pengabdian", "Pengabdian"];

const FIELDS = [
  { key: "username", label: "Username Pembimbing (ID)", type: "text", required: true },
  { key: "password", label: "Password Pembimbing", type: "text", required: true },
  { key: "nama_lengkap", label: "Nama Lengkap Pembimbing", type: "text" },
  { key: "tanggal_lahir", label: "Tanggal Lahir Pembimbing", type: "date" },
  { key: "alamat", label: "Alamat Lengkap", type: "textarea" },
  { key: "tanggal_bergabung", label: "Tanggal Bergabung dengan GRQ", type: "date" },
  { key: "status_pembimbing", label: "Status Pembimbing", type: "select", options: STATUS_OPTIONS },
];

const EMPTY = () => ({
  username: "", password: "", nama_lengkap: "", tanggal_lahir: "",
  alamat: "", tanggal_bergabung: "", status_pembimbing: "Non Pengabdian",
});

const COLS = [
  { key: "no", label: "No" },
  { key: "username", label: "Username Pembimbing (ID)" },
  { key: "password", label: "Password", isPassword: true },
  { key: "nama_lengkap", label: "Nama Lengkap Pembimbing" },
  { key: "tanggal_lahir", label: "Tanggal Lahir" },
  { key: "alamat", label: "Alamat Lengkap" },
  { key: "tanggal_bergabung", label: "Tgl. Bergabung GRQ" },
  { key: "status_pembimbing", label: "Status" },
];

export default function AdminPembimbing() {
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [modal, setModal] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pembimbing`, { headers: authHeader() });
      setRows(res.data || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout(); navigate("/login", { replace: true });
      } else toast.error("Gagal memuat data pembimbing");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => Object.values(r).some((v) => String(v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const openCreate = () => setModal({ mode: "create", data: EMPTY() });
  const openEdit = (r) => setModal({ mode: "edit", data: { ...EMPTY(), ...r } });

  const saveModal = async () => {
    const d = modal.data;
    setBusyId("modal");
    try {
      if (modal.mode === "create") {
        if (!d.username || !d.password) {
          toast.error("Username dan password wajib diisi"); setBusyId(null); return;
        }
        const res = await axios.post(`${API}/pembimbing`, d, { headers: authHeader() });
        toast.success(`Pembimbing ${res.data.username} ditambahkan`);
      } else {
        const patch = { ...d };
        if (!patch.password) delete patch.password;
        const res = await axios.patch(`${API}/pembimbing/${d.id || d.username}`, patch, { headers: authHeader() });
        toast.success(`Data ${res.data.username} diperbarui`);
      }
      setModal(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal menyimpan data";
      toast.error(typeof msg === "string" ? msg : "Gagal menyimpan");
    } finally { setBusyId(null); }
  };

  const confirmDelete = async () => {
    const r = confirmDel;
    setBusyId(r.id);
    try {
      await axios.delete(`${API}/pembimbing/${r.id || r.username}`, { headers: authHeader() });
      toast.success(`Data ${r.username} dihapus`);
      setConfirmDel(null);
      await load();
    } catch { toast.error("Gagal menghapus"); }
    finally { setBusyId(null); }
  };

  const copyCred = (r) => {
    navigator.clipboard?.writeText(`${r.username} / ${r.password}`);
    toast.success("Kredensial disalin");
  };

  return (
    <main className="min-h-screen bg-paper text-forest">
      <AdminNav user={user} active="pembimbing" />

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="text-xs tracking-[0.4em] uppercase text-gold">Dashboard Admin — Pembimbing</span>
          <h1 className="mt-3 font-serif-display text-forest text-4xl md:text-5xl leading-tight">
            Data Pembimbing,<br /><span className="italic text-gold">amanah bimbingan.</span>
          </h1>
          <p className="mt-4 text-moss max-w-2xl text-sm">
            Kelola akun pembimbing di sini. Nama lengkap yang tercatat akan menjadi pilihan pada kolom
            <b> Nama Pembimbing Aktif/Terakhir</b> di data santri.
          </p>
        </motion.div>

        <div className="mt-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cari nama / username"
                className="pl-9 pr-4 py-2.5 bg-white/70 border border-gold/30 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-gold/40 min-w-[280px]"
                data-testid="pemb-search" />
            </div>
            <button onClick={load} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-forest text-sm hover:bg-gold hover:text-forest transition-colors" data-testid="pemb-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={() => setShowPass((v) => !v)} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-gold/40 text-forest text-sm hover:bg-gold hover:text-forest transition-colors" data-testid="pemb-toggle-pw">
              {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              {showPass ? "Sembunyikan Password" : "Tampilkan Password"}
            </button>
          </div>
          <button onClick={openCreate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors" data-testid="pemb-add">
            <Plus size={16} /> Tambah Pembimbing
          </button>
        </div>

        <div className="mt-6 rounded-2xl border border-gold/30 bg-white/70 backdrop-blur overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-forest text-parchment text-left">
                <tr>
                  <th className="px-3 py-3 font-medium whitespace-nowrap">Aksi</th>
                  {COLS.map((c) => <th key={c.key} className="px-3 py-3 font-medium whitespace-nowrap">{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={COLS.length + 1} className="text-center py-10 text-moss">Memuat data…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={COLS.length + 1} className="text-center py-10 text-moss">Belum ada pembimbing. Klik <b>Tambah Pembimbing</b> untuk mulai.</td></tr>
                ) : (
                  filtered.map((r, i) => (
                    <tr key={r.id} className="border-t border-gold/15 hover:bg-sand/60 transition-colors" data-testid={`pemb-row-${r.username}`}>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => openEdit(r)} className="p-2 rounded-full hover:bg-gold hover:text-forest transition-colors" aria-label="Edit" data-testid={`edit-pemb-${r.username}`}>
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => setConfirmDel(r)} className="p-2 rounded-full hover:bg-red-600 hover:text-white transition-colors" aria-label="Hapus" data-testid={`del-pemb-${r.username}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-moss">{i + 1}</td>
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap">{r.username}</td>
                      <td className="px-3 py-2.5">
                        <span className="inline-flex items-center gap-1">
                          <code>{showPass ? r.password || "—" : "••••••••"}</code>
                          <button onClick={() => copyCred(r)} className="text-moss hover:text-gold p-0.5" aria-label="Salin" data-testid={`copy-cred-pemb-${r.username}`}>
                            <Copy size={12} />
                          </button>
                        </span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.nama_lengkap || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.tanggal_lahir || "—"}</td>
                      <td className="px-3 py-2.5 max-w-[300px] truncate" title={r.alamat}>{r.alamat || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{r.tanggal_bergabung || "—"}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] tracking-widest uppercase ${
                          r.status_pembimbing === "Pengabdian" ? "bg-emerald-100 text-emerald-800" : "bg-sand text-moss"
                        }`}>
                          {r.status_pembimbing || "—"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-3 text-xs text-moss">Total: <b>{filtered.length}</b> pembimbing</p>
      </section>

      <AnimatePresence>
        {modal && (
          <PembimbingModal
            state={modal}
            onChange={(next) => setModal({ ...modal, data: next })}
            onClose={() => setModal(null)}
            onSave={saveModal}
            saving={busyId === "modal"}
          />
        )}
        {confirmDel && (
          <ConfirmDialog record={confirmDel} onCancel={() => setConfirmDel(null)} onConfirm={confirmDelete} busy={busyId === confirmDel.id} />
        )}
      </AnimatePresence>
    </main>
  );
}

function PembimbingModal({ state, onChange, onClose, onSave, saving }) {
  const isCreate = state.mode === "create";
  const d = state.data;
  const set = (k, v) => onChange({ ...d, [k]: v });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" onClick={onClose}
      data-testid="pemb-modal">
      <motion.div initial={{ y: 30, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 30, opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="bg-parchment rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-gold/40 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gold/20 sticky top-0 bg-parchment/95 backdrop-blur">
          <div>
            <span className="text-[10px] tracking-[0.3em] uppercase text-gold">{isCreate ? "Tambah Pembimbing" : "Edit Pembimbing"}</span>
            <h3 className="font-serif-display text-forest text-2xl">{isCreate ? "Data pembimbing baru" : d.nama_lengkap || d.username}</h3>
          </div>
          <button onClick={onClose} className="text-forest hover:text-gold p-2" aria-label="Tutup"><X size={18} /></button>
        </div>

        <div className="px-6 py-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="block text-[10px] tracking-[0.2em] uppercase text-moss mb-1.5">
                {f.label}{f.required ? " *" : ""}
              </label>
              {f.type === "select" ? (
                <select value={d[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                  data-testid={`pemb-modal-${f.key}`}>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : f.type === "textarea" ? (
                <textarea value={d[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={3}
                  className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                  data-testid={`pemb-modal-${f.key}`} />
              ) : f.type === "date" ? (
                <input type="date" value={d[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                  className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                  data-testid={`pemb-modal-${f.key}`} />
              ) : (
                <>
                  <input type="text" value={d[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)}
                    className="w-full px-3 py-2.5 bg-white border border-gold/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold text-forest text-sm"
                    data-testid={`pemb-modal-${f.key}`} autoComplete="off" />
                  {!isCreate && f.key === "password" && <p className="mt-1 text-[10px] text-moss">Kosongkan jika tidak diubah</p>}
                </>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gold/20 sticky bottom-0 bg-parchment/95 backdrop-blur">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full text-forest text-sm hover:bg-sand transition-colors" data-testid="pemb-modal-cancel">Batal</button>
          <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-forest text-parchment text-sm hover:bg-gold transition-colors disabled:opacity-60" data-testid="pemb-modal-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ConfirmDialog({ record, onCancel, onConfirm, busy }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] bg-forest/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="bg-parchment rounded-2xl w-full max-w-md p-6 border border-gold/40 shadow-2xl" onClick={(e) => e.stopPropagation()}
        data-testid="confirm-delete-pemb">
        <h3 className="font-serif-display text-forest text-2xl">Hapus pembimbing?</h3>
        <p className="mt-3 text-sm text-moss">
          Akun <b>{record.username}</b>{record.nama_lengkap ? ` (${record.nama_lengkap})` : ""} akan dihapus permanen.
        </p>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button onClick={onCancel} className="px-5 py-2 rounded-full text-forest text-sm hover:bg-sand transition-colors" data-testid="confirm-cancel-pemb">Batal</button>
          <button onClick={onConfirm} disabled={busy} className="inline-flex items-center gap-2 px-6 py-2 rounded-full bg-red-600 text-white text-sm hover:bg-red-700 transition-colors disabled:opacity-60" data-testid="confirm-ok-pemb">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Hapus
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
